import CenterContainer from "components/CenterContainer";
import RoleGuard from "components/RoleGuard";
import LoadingScreen from "components/ScreenState/Loading";
import ToolPadAppShell from "layouts/AppLayout";
import GlobalLayout from "layouts/GlobalLayout";
import RedirectToDefault from "layouts/RedirectToDefault";
import ErrorPage403 from "pages/error/403";
import ErrorPage404 from "pages/error/404";
import ErrorPage from "pages/error/ErrorPage";
import {type ComponentType, type FC, Suspense} from "react";
import {createBrowserRouter, type RouteObject} from "react-router";
import routes from "router/routes";
import type {AccessPredicate, Page, PageGroup} from "router/types";
import type {Role} from "utils/roles";
import {isPageGroupWithChildren} from "./getNavigation";

const withSuspense = (Component: ComponentType) => {
  const Wrapped: FC = () => (
    <Suspense
      fallback={
        <CenterContainer>
          <LoadingScreen />
        </CenterContainer>
      }>
      <Component />
    </Suspense>
  );

  Wrapped.displayName = `WithSuspense(${Component.displayName || Component.name || "Component"})`;
  return Wrapped;
};

const withRoleGuard = (
  Component: ComponentType,
  roles?: Role[],
  canAccess?: AccessPredicate,
) => {
  const Wrapped: FC = () => (
    <RoleGuard roles={roles} canAccess={canAccess}>
      <Component />
    </RoleGuard>
  );

  Wrapped.displayName = `WithRoleGuard(${Component.displayName || Component.name || "Component"})`;
  return Wrapped;
};

const isRouteObject = (r: RouteObject | null): r is RouteObject => r !== null;

const combineAccess = (
  parent?: AccessPredicate,
  child?: AccessPredicate,
): AccessPredicate | undefined => {
  if (parent && child) return user => parent(user) && child(user);
  return child ?? parent;
};

const buildRoutes = (
  pages: Array<Page | PageGroup>,
  parentRoles?: Role[],
  parentCanAccess?: AccessPredicate,
): RouteObject[] => {
  return pages
    .map(page => {
      const effectiveRoles = page.roles ?? parentRoles;
      const effectiveCanAccess = combineAccess(parentCanAccess, page.canAccess);

      if ("Component" in page) {
        return {
          path: page.segment,
          Component: withSuspense(
            withRoleGuard(page.Component, effectiveRoles, effectiveCanAccess),
          ),
        } as RouteObject;
      }

      if (isPageGroupWithChildren(page)) {
        return {
          path: page.segment,
          children: buildRoutes(
            page.children,
            effectiveRoles,
            effectiveCanAccess,
          ),
        } as RouteObject;
      }

      return null;
    })
    .filter(isRouteObject);
};

const router = createBrowserRouter([
  {
    Component: withSuspense(GlobalLayout),
    children: [
      {
        path: "/",
        Component: withSuspense(ToolPadAppShell),
        errorElement: <ErrorPage />,
        children: [
          {
            index: true,
            Component: RedirectToDefault,
          },
          ...buildRoutes(routes),
        ],
      },
      {path: "/forbidden", Component: ErrorPage403},
      {path: "*", Component: ErrorPage404},
    ],
  },
]);

export default router;
